import type { PageScope } from "./types";

/**
 * Maps URL prefixes to scopes required to land on that route after login.
 * More specific prefixes must appear before generic ones.
 */
const ROUTE_RULES: { prefix: string; scopes: PageScope[] }[] = [
  { prefix: "/forecast", scopes: ["FORECAST"] },
  { prefix: "/cashflow", scopes: ["CASHFLOW"] },
  { prefix: "/transactions", scopes: ["TRANSACTIONS"] },
  { prefix: "/categories", scopes: ["TRANSACTIONS"] },
  { prefix: "/bank-reconciliation", scopes: ["BANK_RECONCILIATION"] },
  { prefix: "/receivables", scopes: ["RECEIVABLES"] },
  { prefix: "/supplier-invoices", scopes: ["SUPPLIER_INVOICES"] },
  { prefix: "/invoices-for-payment", scopes: ["SUPPLIER_INVOICES"] },
  { prefix: "/invoice", scopes: ["RECEIVABLES"] },
  { prefix: "/customers", scopes: ["RECEIVABLES"] },
  { prefix: "/archive", scopes: ["ARCHIVE"] },
  { prefix: "/files-integrations", scopes: ["FILES_INTEGRATIONS"] },
  { prefix: "/settings", scopes: ["SETTINGS"] },
  { prefix: "/analytics", scopes: ["ANALYTICS"] },
];

function pathMatchesPrefix(pathLower: string, prefix: string): boolean {
  return pathLower === prefix || pathLower.startsWith(`${prefix}/`);
}

/** Dashboard home – only the bare `/` path (not every path starting with `/`). */
const ROOT_SCOPES: PageScope[] = ["DASHBOARDS"];

export function scopesForPath(pathname: string): PageScope[] | null {
  const p = pathname.toLowerCase();
  if (p === "/" || p === "") {
    return ROOT_SCOPES;
  }
  for (const rule of ROUTE_RULES) {
    if (pathMatchesPrefix(p, rule.prefix)) {
      return rule.scopes;
    }
  }
  return null;
}

export function userHasScopesForPath(
  pathname: string,
  userScopes: PageScope[] | null | undefined,
): boolean {
  const required = scopesForPath(pathname);
  if (!required?.length) {
    return true;
  }
  const set = new Set(userScopes ?? []);
  return required.every((s) => set.has(s));
}

/** Preferred landing path after login / logo click (first matching scope wins). */
export function defaultHomePath(
  scopes: PageScope[] | null | undefined,
): string {
  const s = new Set(scopes ?? []);
  if (s.has("DASHBOARDS")) return "/";
  if (s.has("CASHFLOW")) return "/cashflow";
  if (s.has("FORECAST")) return "/forecast";
  if (s.has("TRANSACTIONS")) return "/transactions";
  if (s.has("RECEIVABLES")) return "/receivables";
  if (s.has("SUPPLIER_INVOICES")) return "/supplier-invoices";
  if (s.has("BANK_RECONCILIATION")) return "/bank-reconciliation";
  if (s.has("ARCHIVE")) return "/archive";
  if (s.has("FILES_INTEGRATIONS")) return "/files-integrations";
  if (s.has("SETTINGS")) return "/settings";
  if (s.has("ANALYTICS")) return "/analytics";
  if (s.has("ADMIN")) return "/admin";
  return "/unauthorized";
}

type ResolveLoginOptions = {
  /** Admin/Trial users reach `/admin` by role, not necessarily `page_scopes`. */
  canAccessAdminRoute?: boolean;
};

/**
 * Honor `from` when the user has all scopes needed for that path; otherwise
 * fall back to {@link defaultHomePath}.
 */
export function resolvePostLoginPath(
  fromPath: string | undefined,
  userScopes: PageScope[] | null | undefined,
  options?: ResolveLoginOptions,
): string {
  const raw = (fromPath ?? "/").split("?")[0] || "/";
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  if (path === "/login" || path === "/mfa" || path === "/unauthorized") {
    return defaultHomePath(userScopes);
  }
  if (
    options?.canAccessAdminRoute &&
    (path === "/admin" || path.startsWith("/admin/"))
  ) {
    return path;
  }
  if (userHasScopesForPath(path, userScopes)) {
    return path;
  }
  return defaultHomePath(userScopes);
}
